// src/sections/Tutorials.tsx

import React, { useState } from 'react';
import clsx from 'clsx';
import { Box, Puzzle, Cuboid } from 'lucide-react';
import Unity from 'logos/Unity';
import GameMaker from 'logos/GameMaker';
import Unreal from 'logos/Unreal';
import Phaser from 'logos/Phaser';

import PortfolioItem from '../components/PortfolioItem';

type TutorialsFilter = 'all' | 'unity' | 'gamemaker' | 'unreal' | 'phaser';

// Each tutorial item should have a unique identifier—here we’ll use `title`.
export interface TutorialData {
  title: string;
  description: string;
  category: TutorialsFilter;
  icon: React.ReactNode;
  link: string;
  size: 'small' | 'large';
  image?: string;
  video?: string;
}

export const tutorialsData: TutorialData[] = [
    {
      title: 'Stable Diffusion in Unity',
      description: 'A dive into image generation for environments and scene organization.',
      category: 'unity',
      icon: <Unity className="w-4 h-4" />,
      video: 'assets/tutorials/tree.mp4',
      link: 'tutorials/stable-diffusion-in-unity',
      size: 'small',
    },
    {
      title: 'Simple Interactive Doors',
      description: 'Covers timeline-based door animations and triggers for dynamic environments.',
      category: 'unity',
      icon: <Unreal className="w-4 h-4" />,
      video: 'assets/tutorials/door.mp4',
      link: 'tutorials/door-interaction',
      size: 'small',
    },
    {
      title: 'Deterministic Physics',
      description: 'Modelling future kinetic outcomes with path traces in a cup scene.',
      category: 'unity',
      icon: <GameMaker className="w-4 h-4" />,
      video: 'assets/tutorials/cup.mp4',
      link: 'tutorials/physics-3d-in-unity',
      size: 'small',
    },
    {
      title: 'Realm of the Mad Frog',
      description: 'Parody tutorial to illustrate how to design fast-paced bullet hells.',
      category: 'gamemaker',
      icon: <GameMaker className="w-4 h-4" />,
      video: 'assets/tutorials/rotmf.mp4',
      link: 'tutorials/realm-of-the-mad-frog',
      size: 'small',
    },
    {
      title: 'Websocket Multiplayer',
      description: 'Designing efficient P2P networked lobbies via websockets.',
      category: 'phaser',
      icon: <Phaser className="w-4 h-4" />,
      video: 'assets/tutorials/multiplayer.mp4',
      link: 'tutorials/websocket-multiplayer',
      size: 'small',
    },
    {
      title: 'Volition',
      description: 'Parody tutorial to introduce game mock-ups, GUI, and loading screens.',
      category: 'unity',
      icon: <GameMaker className="w-4 h-4" />,
      video: 'assets/tutorials/volition.mp4',
      link: 'tutorials/volition',
      size: 'small',
    },
    {
      title: 'Generative Dialogue',
      description: 'Using Markov chains to generate dialogue for NPCs in a game.',
      category: 'unity',
      icon: <Unity className="w-4 h-4" />,
      video: 'assets/tutorials/llm.mp4',
      link: 'tutorials/generative-dialogue',
      size: 'small',
    },
];

const Tutorials: React.FC = () => {
  const [filter, setFilter] = useState<TutorialsFilter>('all');

  // Filtered tutorials
  const filteredTutorials = tutorialsData.filter((item) => {
    if (filter === 'all') return true;
    return item.category === filter;
  });

  // Filter buttons, all-lowercase
  const filterButtons = [
    { label: 'all', value: 'all' },
    { label: 'unity', value: 'unity', icon: <Unity className="w-4 h-4" /> },
    { label: 'gamemaker', value: 'gamemaker', icon: <GameMaker className="w-4 h-4" /> },
    // { label: 'unreal', value: 'unreal', icon: <Unreal className="w-4 h-4" /> },
    { label: 'phaser', value: 'phaser', icon: <Phaser className="w-4 h-4" /> },
  ];

  return (
    <section id="tutorials" className="section">
      <div className="max-w-7xl mx-auto px-4 py-20">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-8">
          Tutorials
        </h1>

        {/* Filter Buttons */}
        <div className="flex justify-center mb-8 space-x-4 flex-wrap">
          {filterButtons.map((btn) => (
            <button
              key={btn.value}
              onClick={() => setFilter(btn.value as TutorialsFilter)}
              className={clsx(
                'filter-btn px-4 py-2 rounded-full flex items-center space-x-2 transition-colors mb-2',
                {
                  'bg-blue-600 text-white hover:bg-blue-700': filter === btn.value,
                  'bg-gray-200 dark:bg-gray-700 hover:bg-blue-600 hover:text-white':
                    filter !== btn.value,
                }
              )}
            >
              {btn.icon}
              <span>{btn.label}</span>
            </button>
          ))}
        </div>

        {/* Tutorials Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {filteredTutorials.map((item) => (
            <PortfolioItem
              key={item.title} // Using the title as the stable key
              data={item}
              className={clsx(
                'col-span-1',
                item.size === 'large' && 'sm:col-span-2'
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Tutorials;
